import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'al-community-saved-searches';
const CHANGE_EVENT = 'al-community-saved-searches-change';
const MAX_SAVED = 12;

export interface SavedCommunitySearch {
  id: string;
  label: string;
  /** URLSearchParams string without the leading "?" */
  query: string;
  createdAt: number;
}

function read(): SavedCommunitySearch[] {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed)
      ? parsed.filter((item) => item && typeof item.query === 'string' && typeof item.label === 'string')
      : [];
  } catch {
    return [];
  }
}

function write(items: SavedCommunitySearch[]) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_SAVED)));
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

let cachedKey = '';
let cachedItems: SavedCommunitySearch[] = [];
const EMPTY: SavedCommunitySearch[] = [];

function getSnapshot() {
  const items = read();
  const key = JSON.stringify(items);
  if (key !== cachedKey) {
    cachedKey = key;
    cachedItems = items;
  }
  return cachedItems;
}

function subscribe(onChange: () => void) {
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

export function useSavedCommunitySearches(): SavedCommunitySearch[] {
  return useSyncExternalStore(subscribe, getSnapshot, () => EMPTY);
}

export function saveCommunitySearch(label: string, query: string): boolean {
  const items = read();
  if (items.some((item) => item.query === query)) return false;
  write([{ id: `${Date.now()}`, label, query, createdAt: Date.now() }, ...items]);
  return true;
}

export function removeCommunitySearch(id: string) {
  write(read().filter((item) => item.id !== id));
}
